import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  BellRing,
  Clock3,
  Database,
  FolderKanban,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";
import AppPageLoader from "../../components/AppPageLoader";
import {
  formatTimestamp,
  getAccountSetRequestTypeMeta,
  formatUptime,
  loadAdminOverviewData,
} from "./adminShared";
import "./AdminShared.css";
import "./AdminOverview.css";

export default function AdminOverview() {
  const [data, setData] = useState({
    stats: null,
    systemHealth: null,
    requests: [],
    notifications: [],
    notificationsUnread: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchOverview = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const next = await loadAdminOverviewData();
      setData({
        stats: next.stats,
        systemHealth: next.systemHealth,
        requests: next.requests,
        notifications: next.notifications,
        notificationsUnread: next.notificationsUnread,
      });
      setError(
        next.errors.length
          ? `Some overview data could not be loaded: ${next.errors.join(", ")}.`
          : "",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const pendingRequestsCount = useMemo(
    () => data.requests.filter((request) => request.status === "pending").length,
    [data.requests],
  );

  const recentRequests = useMemo(() => data.requests.slice(0, 5), [data.requests]);

  const recentNotifications = useMemo(() => data.notifications.slice(0, 5), [data.notifications]);

  const nodeVersionLabel = String(data.systemHealth?.system?.nodeVersion || "n/a").replace(
    /^v/i,
    "",
  );
  const databaseStatusLabel = String(data.systemHealth?.database?.status || "n/a");
  const formattedDatabaseStatus =
    databaseStatusLabel === "n/a"
      ? "n/a"
      : databaseStatusLabel.charAt(0).toUpperCase() + databaseStatusLabel.slice(1);
  const rawLatency = Number(data.systemHealth?.database?.latency);
  const databaseLatencyLabel =
    Number.isFinite(rawLatency) && rawLatency > 0 ? `${rawLatency} ms` : "n/a";

  const summaryCards = [
    {
      icon: Clock3,
      label: "Uptime",
      value: formatUptime(data.systemHealth?.system?.uptime),
      meta: `Node ${nodeVersionLabel}`,
    },
    {
      icon: Database,
      label: "Database",
      value: formattedDatabaseStatus,
      meta: `Latency ${databaseLatencyLabel}`,
    },
    {
      icon: FolderKanban,
      label: "Account Sets",
      value: data.stats?.totalAccountSets ?? "-",
      meta: `${data.stats?.totalBrokers ?? 0} brokers linked`,
    },
    {
      icon: BellRing,
      label: "Collector Setup",
      value: data.notificationsUnread,
      meta: "Unread setup notifications",
    },
  ];

  if (loading) {
    return (
      <div className="admin-page admin-overview-page admin-overview-page-loading">
        <AppPageLoader
          label="Loading Overview"
          detail="Preparing admin metrics, queues, and recent activity..."
        />
      </div>
    );
  }

  return (
    <div className="admin-page admin-overview-page">
      <div className="admin-overview-header">
        <div>
          <h1>
            <Activity className="admin-overview-header-icon" />
            <span>Overview</span>
          </h1>
          <p>
            Track system health, activity queues, and the admin workload from one lightweight
            summary page.
          </p>
        </div>
        <div className="admin-page-actions">
          <button
            className="admin-btn admin-btn-primary admin-overview-refresh-btn"
            onClick={() => fetchOverview(true)}
            disabled={loading || refreshing}
          >
            <RefreshCcw size={15} />
            {refreshing ? "Refreshing..." : "Refresh Overview"}
          </button>
        </div>
      </div>

      {error && <div className="admin-inline-alert error">{error}</div>}

      <>
        <div className="admin-overview-summary-grid">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="admin-overview-summary-card">
                <div className="admin-overview-summary-icon">
                  <Icon size={20} />
                </div>
                <div className="admin-overview-summary-copy">
                  <span>{card.label}</span>
                  <strong>{card.value}</strong>
                  <small>{card.meta}</small>
                </div>
              </div>
            );
          })}
        </div>

        <div className="admin-overview-layout">
          <div className="admin-overview-column">
            <section className="admin-card admin-overview-card">
              <div className="admin-card-heading">
                <div>
                  <div className="admin-card-title">
                    <BellRing />
                    <span>Queue Snapshot</span>
                  </div>
                  <p>Active admin work that likely needs attention.</p>
                </div>
                <span className="admin-badge admin-badge-warning">
                  <BellRing size={12} />
                  {pendingRequestsCount + data.notificationsUnread}
                </span>
              </div>

              <div className="admin-overview-detail-grid">
                <div className="admin-overview-detail-card">
                  <span>Unread Collector</span>
                  <strong>{data.notificationsUnread}</strong>
                </div>
                <div className="admin-overview-detail-card">
                  <span>Pending Requests</span>
                  <strong>{pendingRequestsCount}</strong>
                </div>
                <div className="admin-overview-detail-card">
                  <span>Regular Users</span>
                  <strong>{data.stats?.regularUsers ?? 0}</strong>
                </div>
                <div className="admin-overview-detail-card">
                  <span>Total Brokers</span>
                  <strong>{data.stats?.totalBrokers ?? 0}</strong>
                </div>
              </div>
            </section>
            <section className="admin-card admin-overview-card">
              <div className="admin-card-heading">
                <div>
                  <div className="admin-card-title">
                    <BellRing />
                    <span>Recent Collector Notifications</span>
                  </div>
                  <p>Latest setup items that may need admin-side collector deployment.</p>
                </div>
              </div>

              {data.notifications.length === 0 ? (
                <div className="admin-empty">No collector setup notifications.</div>
              ) : (
                <div className="admin-overview-feed">
                  {recentNotifications.map((notification) => (
                    <div key={notification.id} className="admin-overview-feed-item">
                      <div className="admin-overview-feed-main">
                        <div className="admin-overview-feed-title">
                          <span
                            className={`admin-badge ${notification.status === "unread" ? "admin-badge-info" : "admin-badge-neutral"}`}
                          >
                            {notification.status || "read"}
                          </span>
                          <strong>
                            {notification.payload?.accountSet?.name ||
                              notification.accountSet?.name ||
                              "Unknown account set"}
                          </strong>
                        </div>
                        <div className="admin-overview-feed-meta">
                          <span>
                            User:{" "}
                            {notification.payload?.user?.email ||
                              notification.user?.email ||
                              "Unknown"}
                          </span>
                          <span>Linked: {formatTimestamp(notification.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <section className="admin-card admin-overview-card admin-overview-card-tall">
            <div className="admin-card-heading">
              <div>
                <div className="admin-card-title">
                  <ShieldCheck />
                  <span>Recent Account Requests</span>
                </div>
                <p>Recent approval queue items across link, modify, and delete requests.</p>
              </div>
            </div>

            {recentRequests.length === 0 ? (
              <div className="admin-empty">No recent requests.</div>
            ) : (
              <div className="admin-overview-feed">
                {recentRequests.map((request) => (
                  <div key={request.id} className="admin-overview-feed-item">
                    <div className="admin-overview-feed-main">
                      <div className="admin-overview-feed-title">
                        <span
                          className={`admin-badge ${
                            request.status === "approved"
                              ? "admin-badge-success"
                              : request.status === "rejected"
                                ? "admin-badge-danger"
                                : "admin-badge-warning"
                          }`}
                        >
                          {request.status}
                        </span>
                        <strong>{getAccountSetRequestTypeMeta(request.type).overviewLabel}</strong>
                      </div>
                      <div className="admin-overview-feed-meta">
                        <span>User: {request.User?.email || "Unknown"}</span>
                        <span>Set: {request.AccountSet?.name || "Unknown"}</span>
                        <span>Submitted: {formatTimestamp(request.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </>
    </div>
  );
}
